/**
 * MDI Icon Alias Resolution
 *
 * Uses a pre-generated alias map for efficient alias resolution.
 * The map is generated from @mdi/svg metadata at build time.
 *
 * The webfont CSS only contains classes for canonical names (e.g., `mdi-note-plus`),
 * not aliases (e.g., `note-add`). This module resolves aliases at runtime.
 *
 * Also normalizes underscores to hyphens (e.g., `delete_forever` -> `delete-forever`)
 * since MDI uses hyphens but some models may use underscores.
 *
 * To regenerate the alias map after updating @mdi/svg:
 *   pnpm generate:icons
 */

import { MDI_ALIAS_MAP } from "./mdi-alias-map.generated";

/**
 * Normalizes an icon name by converting underscores to hyphens.
 * MDI uses hyphens (e.g., `delete-forever`) but models may use underscores.
 */
function normalizeIconName(name: string): string {
	return name.replace(/_/g, "-");
}

/**
 * Resolves an icon name to its canonical MDI name.
 *
 * 1. Normalizes underscores to hyphens (e.g., `delete_forever` -> `delete-forever`)
 * 2. If the result is an alias, returns the canonical name
 * 3. Otherwise returns the normalized name unchanged
 *
 * @example
 * resolveIconName('note-add')       // returns 'note-plus' (alias resolved)
 * resolveIconName('note_add')       // returns 'note-plus' (normalized + alias resolved)
 * resolveIconName('delete_forever') // returns 'delete-forever' (normalized)
 * resolveIconName('home')           // returns 'home' (already canonical)
 * resolveIconName('unknown-icon')   // returns 'unknown-icon' (passthrough)
 */
export function resolveIconName(name: string): string {
	const normalized = normalizeIconName(name);
	return MDI_ALIAS_MAP[normalized] ?? normalized;
}

/**
 * Checks if a name is an alias (not a canonical icon name).
 * Normalizes underscores to hyphens before checking.
 */
export function isAlias(name: string): boolean {
	return normalizeIconName(name) in MDI_ALIAS_MAP;
}

/**
 * Gets all aliases for a canonical icon name.
 * Returns empty array if no aliases exist.
 *
 * Note: This requires iterating the map. For frequent lookups,
 * consider caching the result.
 */
export function getAliases(canonicalName: string): string[] {
	const aliases: string[] = [];
	for (const [alias, canonical] of Object.entries(MDI_ALIAS_MAP)) {
		if (canonical === canonicalName) {
			aliases.push(alias);
		}
	}
	return aliases;
}
